import { Card } from "primereact/card";
import {
  CustomAction,
  getAddressColumns,
  SovColumnId,
  SovColumnProps,
  StreamingSource,
} from "./AddressCardColumns";
import * as client from "../../../client";
import { formatExactMoneyDollars } from "../Money";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import styles from "./AddressCard.module.css";
import { resolveConstructionCodeEdit } from "./construction";
import { ReprocessDialog } from "./ReprocessDialog";
import {
  useDeductibleSummary,
  useIntegrationData,
  useLimitSummary,
  useLocalUser,
  useThisReportIsUpdating,
  useUpdateAddresses,
  useUpdateReport,
  useLocationClassifications,
} from "../data";
import {
  applyDeltasAsOverride,
  applyOverridesToAddress,
  applyOverridesToResponse,
} from "../Conversion";
import { ThemeContext } from "../../../themes/Theme";
import { Tooltip } from "primereact/tooltip";
import { flagTextFor, maxSeverityForAddressFlags } from "./flags";
import { groupBy, isEqual } from "lodash";
import { BulkEditModal, BulkEditModalState } from "./BulkEditModal";
import { StreetViewModal, StreetViewModalState } from "./StreetViewModal";
import { flaggedHighlightColor } from "../dashboard/util";
import { v4 as uuid } from "uuid";
import { useLogUIEventCallback } from "../../../amplitude";
import { EditSheets } from "./EditSheets";
import { ReconciliationFunctionModal } from "./ReconciliationFunctionModal";
import { isRequiredField } from "./required";
import { confirmDialog } from "primereact/confirmdialog";
import { ClientId, featureEnabled } from "../../../client-features";
import { useClientId } from "../../../ClientConfig";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { useRerunningMessage } from "../RerunUI";
import {
  RawAddressWithIndices,
  SovGridContext,
  StreamingStatus,
} from "./types";
import { ColumnEditModal } from "./ColumnEditModal";
import { useSovGridState } from "./SovGridStateContext";
import { sovViewById } from "./sovViews";
import { SovViewSwitcher } from "./SovViewSwitcher";
import { AddAddressModal } from "./AddAddressModal";
import { SetFilter } from "./SetFilter";
import { blendedItvPerSqft } from "./utils";
import { AgGridReact } from "ag-grid-react";
import { PivotPanel } from "./PivotPanel";
import { Dialog } from "primereact/dialog";
import {
  AllCommunityModule,
  ColDef,
  ColGroupDef,
  CellEditingStoppedEvent,
  CellFocusedEvent,
  IHeaderParams,
  RowClassParams,
  StateUpdatedEvent,
  themeQuartz,
  colorSchemeDark,
  colorSchemeLight,
} from "ag-grid-community";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import { FilterableSelectCellEditor } from "./CellEditorsAg";
import {
  downloadReportHistory,
  LocationClassification,
  LocationClassificationKey,
} from "../../../api/routes";
import { downloadBlob, useDeepMemoValue } from "../../../util";

const COVERAGE_FIELDS: Set<keyof client.RawAddress> = new Set([
  "tiv",
  "building_value",
  "bpp_value",
  "bi_incl_ee_value",
  "bi_excl_ee_value",
  "tenant_betterments_value",
  "signs_value",
  "furniture_and_fixtures_value",
  "inventory_value",
  "contractors_equipment_value",
  "outdoor_property_and_autos_value",
]);

const naturalSortComparator = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  const strA = String(a);
  const strB = String(b);

  // Split into segments of text and numbers
  const segmentRegex = /(\d+|\D+)/g;
  const partsA = strA.match(segmentRegex) ?? [];
  const partsB = strB.match(segmentRegex) ?? [];

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i];
    const partB = partsB[i];

    if (!partA) return -1;
    if (!partB) return 1;

    const numA = Number(partA);
    const numB = Number(partB);
    const bothNumeric = !isNaN(numA) && !isNaN(numB);

    if (bothNumeric) {
      if (numA !== numB) return numA - numB;
    } else {
      const cmp = partA.localeCompare(partB);
      if (cmp !== 0) return cmp;
    }
  }

  return 0;
};

const computeFilteredTotal = (
  api: IHeaderParams["api"],
  field: string
): number => {
  let total = 0;
  api.forEachNodeAfterFilter((node) => {
    if (!node.data) return;
    const val = (node.data as Record<string, unknown>)[field];
    if (val != null && !isNaN(Number(val))) {
      total += Number(val);
    }
  });
  return total;
};

const streamingStatusForSource = (
  source: StreamingSource,
  context: SovGridContext
): StreamingStatus => {
  switch (source) {
    case "integration":
      return context.integrationStatus;
    case "deductible":
      return context.deductibleStatus;
    case "limit":
      return context.limitStatus;
  }
};

const computeFilteredTotalByGetValue = (
  api: IHeaderParams["api"],
  getValue: SovColumnProps["getValue"],
  context: SovGridContext
): number => {
  let total = 0;
  api.forEachNodeAfterFilter((node) => {
    if (!node.data) return;
    const val = getValue(node.data as RawAddressWithIndices, context);
    if (typeof val === "number" && !isNaN(val)) {
      total += val;
    }
  });
  return total;
};

// Overall (blended) ITV ($/sqft) across the grid's currently-filtered rows.
// Delegates to the shared helper so this matches the referral snapshot's ITV.
const computeFilteredAverageItv = (
  api: IHeaderParams["api"]
): number | undefined => {
  const rows: RawAddressWithIndices[] = [];
  api.forEachNodeAfterFilter((node) => {
    if (node.data) rows.push(node.data);
  });
  return blendedItvPerSqft(rows) ?? undefined;
};

const SovHeader = (
  params: IHeaderParams & {
    columnInfo: SovColumnProps;
    isSortable: boolean;
    onBulkEdit: (columnInfo: SovColumnProps) => void;
  }
) => {
  const { columnInfo, isSortable, onBulkEdit } = params;

  const field = columnInfo.field;
  const isCoverageField = !!field && COVERAGE_FIELDS.has(field);

  const [sortDirection, setSortDirection] = useState(params.column.getSort());

  const [filterActive, setFilterActive] = useState(
    params.column.isFilterActive()
  );

  useEffect(() => {
    const sortListener = () => setSortDirection(params.column.getSort());
    params.column.addEventListener("sortChanged", sortListener);
    return () => params.column.removeEventListener("sortChanged", sortListener);
  }, [params.column]);

  useEffect(() => {
    const filterListener = () =>
      setFilterActive(params.column.isFilterActive());
    params.column.addEventListener("filterActiveChanged", filterListener);
    return () =>
      params.column.removeEventListener("filterActiveChanged", filterListener);
  }, [params.column]);

  const handleSortClick = () => {
    if (isSortable) {
      params.progressSort(false);
    }
  };

  // Compute the header total directly from the grid's filtered rows.
  // Streaming-sourced columns show Loading/Error states instead of a partial
  // total — see streamingStatusForColumn.
  const context = params.context as SovGridContext;
  let headerTotalValue: string | undefined;
  if (columnInfo.columnName === "ITV") {
    const average = computeFilteredAverageItv(params.api);
    if (average !== undefined) {
      headerTotalValue = formatExactMoneyDollars(average);
    }
  } else if (isCoverageField && field) {
    const total = computeFilteredTotal(params.api, field);
    if (total !== 0) {
      headerTotalValue = formatExactMoneyDollars(total, {
        withRounding: true,
        withDollarSign: true,
      });
    }
  } else if (columnInfo.showHeaderSum) {
    const status = columnInfo.streamingSource
      ? streamingStatusForSource(columnInfo.streamingSource, context)
      : null;
    if (status?.isLoading) {
      headerTotalValue = `Loading [${status.loadedChunks}/${status.totalChunks}]`;
    } else if (status?.hasError) {
      headerTotalValue = "Error";
    } else {
      const total = computeFilteredTotalByGetValue(
        params.api,
        columnInfo.getValue,
        context
      );
      if (total !== 0) {
        headerTotalValue = formatExactMoneyDollars(total, {
          withRounding: true,
          withDollarSign: true,
        });
      }
    }
  }
  const hasHeaderValue = !!headerTotalValue;

  return (
    <div
      className={styles.headerWithTotal}
      onClick={handleSortClick}
      style={{ cursor: isSortable ? "pointer" : "default" }}
    >
      <div className={styles.header}>
        {columnInfo.bulkEdit ? (
          <div
            className={styles.bulkEditColumnHeader}
            onClick={(e) => {
              e.stopPropagation();
              onBulkEdit(columnInfo);
            }}
          >
            {columnInfo.headerLabel ?? columnInfo.columnName}{" "}
          </div>
        ) : (
          <>{columnInfo.headerLabel ?? columnInfo.columnName} </>
        )}
        {columnInfo.infoText && (
          <TooltipWrapper tooltipText={columnInfo.infoText} maxWidth={400}>
            <i className="pi pi-info-circle" style={{ fontSize: "12px" }} />
          </TooltipWrapper>
        )}
        {isSortable && sortDirection && (
          <i
            className={
              sortDirection === "asc"
                ? "pi pi-sort-amount-up-alt"
                : "pi pi-sort-amount-down"
            }
            style={{ fontSize: "12px", marginLeft: "2px" }}
          />
        )}
        {columnInfo.filterable && (
          <i
            className={filterActive ? "pi pi-filter-fill" : "pi pi-filter"}
            style={{
              fontSize: "12px",
              marginLeft: "2px",
              cursor: "pointer",
              color: filterActive ? "var(--primary-color)" : undefined,
            }}
            onClick={(e) => {
              e.stopPropagation();
              params.showFilter(e.currentTarget);
            }}
          />
        )}
      </div>
      {hasHeaderValue && (
        <div style={{ color: "var(--primary-color)" }}>{headerTotalValue}</div>
      )}
    </div>
  );
};

type AddressCardProps = {
  specialtyPropertyInfo: client.ReportResponse;
  setMapVisible?: (visible: boolean) => void;
};

const configForAddressAndColumnInfo = (
  clientId: ClientId,
  rowData: RawAddressWithIndices,
  columnInfo: SovColumnProps,
  context: SovGridContext,
  isLightMode: boolean
) => {
  const address = rowData.__extras.fullAddress;
  const value = columnInfo.getValue(rowData, context);
  const hasOverride =
    !!columnInfo.field && columnInfo.field in address.private_overrides;
  const maxSeverity = maxSeverityForAddressFlags(address, columnInfo.field);
  const isBlank = (value ?? "") === "";
  const isRequiredAndIsBlank =
    isBlank && isRequiredField(clientId, columnInfo.field);
  const flagText = flagTextFor(
    address,
    columnInfo.field,
    columnInfo.showTooltipsWhenNotFlagged,
    isRequiredAndIsBlank
  );
  const hasHighlight = maxSeverity || hasOverride || isRequiredAndIsBlank;

  return {
    originalValue: value,
    flagText: flagText ?? columnInfo.getTooltip?.(rowData, context),
    marker: isBlank ? undefined : columnInfo.getMarker?.(rowData, context),
    hasHighlight: hasHighlight,
    highlightColor: flaggedHighlightColor(
      maxSeverity,
      isLightMode,
      hasOverride,
      isRequiredAndIsBlank
    ),
    isBlank: isBlank,
  };
};

type CellBodyProps = {
  columnInfo: SovColumnProps;
  rowData: RawAddressWithIndices;
  context: SovGridContext;
  isLightMode: boolean;
  isUpdatingAddress: boolean;
  isDisabled: boolean;
  onClick?: () => void;
  tooltipOverride?: string;
};

const CellBody: React.FC<CellBodyProps> = ({
  columnInfo,
  rowData,
  context,
  isLightMode,
  isUpdatingAddress,
  isDisabled,
  onClick,
  tooltipOverride,
}) => {
  const isUpdating = isUpdatingAddress;
  const clientId = useClientId();
  const {
    originalValue,
    flagText,
    marker,
    hasHighlight,
    highlightColor,
    isBlank,
  } = configForAddressAndColumnInfo(
    clientId,
    rowData,
    columnInfo,
    context,
    isLightMode
  );
  let value: string | undefined | null;
  if (columnInfo.renderNumberWithCommas && originalValue != null) {
    const num = Number(originalValue);
    value = !isNaN(num) ? num.toLocaleString() : String(originalValue);
  } else if (typeof originalValue === "number") {
    value = formatExactMoneyDollars(originalValue, {
      withRounding: true,
      withDollarSign: true,
    });
  } else {
    value = originalValue;
  }

  const tooltipId = `tooltip-${uuid()}`;

  return (
    <div
      className={styles.cellTextWrapper}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "unset" }}
    >
      <Tooltip
        target={`#${tooltipId}`}
        position="left"
        style={{ maxWidth: "600px" }}
      />
      <span
        className={styles.cellText}
        id={tooltipId}
        style={
          hasHighlight
            ? {
                backgroundColor: highlightColor,
                padding: "2px",
                opacity: isUpdating || isDisabled ? "0.5" : "1.0",
                width: isBlank ? "40px" : "auto",
                height: isBlank ? "23px" : "auto",
              }
            : {
                opacity: isUpdating || isDisabled ? "0.5" : "1.0",
              }
        }
        data-pr-tooltip={tooltipOverride ?? flagText}
        data-pr-showdelay={500}
      >
        {columnInfo.isIconValue ? (
          value === "Has Lat/Long" ? (
            <span className="pi pi-map" />
          ) : null
        ) : (
          <>
            {value}
            {marker && (
              <span
                className={styles.cellMarker}
                style={{ backgroundColor: marker.color }}
                role="img"
                aria-label={marker.label}
              />
            )}
          </>
        )}
      </span>
    </div>
  );
};

// AG Grid theme configuration
const agThemeLight = themeQuartz.withPart(colorSchemeLight);
const agThemeDark = themeQuartz.withPart(colorSchemeDark);

// AG Grid cells have ~24px of internal padding that PrimeReact columns didn't have.
// Add this to each column width so text doesn't get clipped.
const AG_GRID_COL_PADDING = 24;

// Colors for collapsible group expander icons.
const COLLAPSIBLE_GROUP_COLORS: Record<string, string> = {
  hazard: "#6366f1", // indigo
  "RMS GU AAL": "#0ea5e9", // sky blue
  "AIR GU AAL": "#ef4444", // red
  "KAT GU AAL": "#84cc16", // lime
  "HazardHub Peril Scores": "#f59e0b", // amber
  "HH COPE": "#10b981", // emerald
  Flood: "#0891b2", // cyan — KatRisk-sourced FEMA zone + flood score
  Nearmap: "#d946ef", // fuchsia
  "Cape Analytics": "#7c3aed", // purple
  "Luminor Replacement Cost": "#3b82f6", // blue
  Deductibles: "#ec4899", // pink
  Limits: "#8b5cf6", // violet
  "WS Secondary Modifiers": "#f97316", // orange
  "EQ Secondary Modifiers": "#14b8a6", // teal
};

// Short labels shown next to the chevron so users can tell what will expand.
const COLLAPSIBLE_GROUP_SHORT_LABELS: Record<string, string> = {
  hazard: "Hazard",
  "RMS GU AAL": "RMS",
  "AIR GU AAL": "AIR",
  "KAT GU AAL": "KAT",
  "HazardHub Peril Scores": "Perils",
  "HH COPE": "COPE",
  Flood: "Flood",
  Nearmap: "Nearmap",
  "Cape Analytics": "Cape",
  "Luminor Replacement Cost": "RC",
  Deductibles: "Ded",
  Limits: "Limits",
  "WS Secondary Modifiers": "WS",
  "EQ Secondary Modifiers": "EQ",
};

// Columns that should be pinned to the left when the freeze toggle is on.
const FROZEN_COLUMN_IDS = new Set<SovColumnId>([
  "derived.map",
  "broker.premises_number",
  "broker.building_number",
  "broker.street_address",
  "broker.city",
  "broker.state",
]);

const INTEGRATION_DATA_TYPES: client.IntegrationDataRequest["types"] = [
  "geographic_enrichment",
  "rms",
  "air",
  "spatialkat",
  "hazard_hub",
  "nearmap",
  "nearmap_ai",
  "cape",
  "sindri",
];

export const AddressCard: React.FC<AddressCardProps> = ({
  specialtyPropertyInfo,
  setMapVisible,
}) => {
  const clientId = useClientId();
  const safeResponse = useMemo(
    () => applyOverridesToResponse(specialtyPropertyInfo, clientId),
    [specialtyPropertyInfo, clientId]
  );
  const reportId = specialtyPropertyInfo.report_id;
  const sovs = specialtyPropertyInfo.report_json.sovs;
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const [editedColumnInfo, setEditedColumnInfo] =
    useState<SovColumnProps | null>(null);

  const updateReport = useUpdateReport(reportId);
  const updateAddresses = useUpdateAddresses(reportId);

  // While an update is in flight, show the optimistic sovs from the mutation
  // variables; otherwise show what the server returned.
  const effectiveSovs = useMemo(
    () =>
      updateReport.isPending ? (updateReport.variables.sovs ?? sovs) : sovs,
    [sovs, updateReport.isPending, updateReport.variables?.sovs]
  );

  const fullAddresses: RawAddressWithIndices[] = useMemo(() => {
    return effectiveSovs
      .filter((sov) => sov.is_enabled)
      .flatMap((sov) =>
        sov.addresses.map((address) => ({
          ...applyOverridesToAddress(address),
          __extras: {
            fullAddress: address,
          },
        }))
      );
  }, [effectiveSovs]);

  // Stabilized so a parent re-render with same-content sovs (e.g. an
  // optimistic update that matches the server state) doesn't reset the
  // chunked-summary hooks' merged maps.
  const enabledAddressIds = useDeepMemoValue(
    useMemo(
      () => fullAddresses.map((a) => a.__extras.fullAddress.address_id),
      [fullAddresses]
    )
  );

  // Fetch integration data (non-blocking)
  const integrationResult = useIntegrationData(
    reportId,
    INTEGRATION_DATA_TYPES,
    enabledAddressIds
  );
  const integrationByAddressId = useMemo(
    () => integrationResult.data?.addresses ?? {},
    [integrationResult.data]
  );
  const integrationStatus = useDeepMemoValue<StreamingStatus>({
    isLoading: integrationResult.isLoading,
    hasError: integrationResult.hasError,
    loadedChunks: integrationResult.loadedChunks,
    totalChunks: integrationResult.totalChunks,
  });

  // Fetch deductible summary (non-blocking) — drives the Deductibles columns.
  const deductibleResult = useDeductibleSummary(reportId, enabledAddressIds);
  const deductibleByAddressId = useMemo(
    () => deductibleResult.data?.addresses ?? {},
    [deductibleResult.data]
  );
  const deductibleStatus = useDeepMemoValue<StreamingStatus>({
    isLoading: deductibleResult.isLoading,
    hasError: deductibleResult.hasError,
    loadedChunks: deductibleResult.loadedChunks,
    totalChunks: deductibleResult.totalChunks,
  });

  // Fetch location classification (non-blocking) — drives the pivot panel's
  // wind/EQ gate grouping and row tinting.
  const classificationLocations = useMemo(
    () => fullAddresses.map((a) => ({ state: a.state, county: a.county })),
    [fullAddresses]
  );
  const classificationResult = useLocationClassifications(
    classificationLocations
  );
  const classificationByLocation = useMemo(
    () =>
      classificationResult.data ??
      new Map<LocationClassificationKey, LocationClassification>(),
    [classificationResult.data]
  );

  // Fetch limit summary (non-blocking) — drives the Limits columns.
  const limitResult = useLimitSummary(reportId, enabledAddressIds);
  const limitByAddressId = useMemo(
    () => limitResult.data?.addresses ?? {},
    [limitResult.data]
  );
  const limitStatus = useDeepMemoValue<StreamingStatus>({
    isLoading: limitResult.isLoading,
    hasError: limitResult.hasError,
    loadedChunks: limitResult.loadedChunks,
    totalChunks: limitResult.totalChunks,
  });

  // Streaming side-data is delivered to cells via AG Grid's `context`, not
  // baked into row data. That way chunk arrivals don't churn row identity —
  // we just call refreshCells() (see effect below) to re-evaluate visible
  // cells against the new maps.
  //
  // Deps include the outer `*Result.data` wrappers in addition to the
  // *ByAddressId maps. The maps come from useChunkedAddressSummary, which
  // mutates a shared object in place across renders — its reference stays
  // stable as new chunks arrive. The outer wrappers, by contrast, are
  // re-memoed each time `version` bumps, so they're the signal that fresh
  // data is available. Without them this memo would never rebuild and AG
  // Grid would never see updated context.
  const sovGridContext: SovGridContext = useMemo(
    () => ({
      integrationByAddressId,
      integrationStatus,
      deductibleByAddressId,
      deductibleStatus,
      limitByAddressId,
      limitStatus,
      classificationByLocation,
      classificationStatus: { isLoading: classificationResult.isLoading },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
    [
      integrationByAddressId,
      integrationStatus,
      deductibleByAddressId,
      deductibleStatus,
      limitByAddressId,
      limitStatus,
      classificationByLocation,
      classificationResult.isLoading,
      integrationResult.data,
      deductibleResult.data,
      limitResult.data,
      classificationResult.data,
    ]
  );

  const unfilteredTotalTIV = useMemo(
    () => fullAddresses.reduce((total, address) => total + address.tiv, 0),
    [fullAddresses]
  );
  const [reprocessDialogVisible, setReprocessDialogVisible] = useState(false);
  const [bulkEditModalField, setBulkEditModalField] =
    useState<BulkEditModalState>(undefined);
  const logUIEvent = useLogUIEventCallback();
  const [addAddressModalVisible, setAddAddressModalVisible] = useState(false);
  const [pivotVisible, setPivotVisible] = useState(false);
  const [streetViewModalVisible, setStreetViewModalVisible] =
    useState<StreetViewModalState>();
  const [streetViewAddress, setStreetViewAddress] =
    useState<client.Address | null>(null);
  const [reconciliationModalVisible, setReconciliationModalVisible] =
    useState(false);
  const currentUser = useLocalUser();
  const overflowMenuRef = useRef<Menu>(null);

  const hasAnyAddressOverrides = useMemo(
    () =>
      sovs.some((sov) =>
        sov.addresses.some(
          (a) => Object.keys(a.private_overrides).length > 0 || a.is_deleted
        )
      ),
    [sovs]
  );

  const clearAllAddressEdits = useCallback(() => {
    const accept = () => {
      const clearedAddresses: client.Address[] = [];
      for (const sov of sovs) {
        for (const address of sov.addresses) {
          if (
            Object.keys(address.private_overrides).length > 0 ||
            address.is_deleted
          ) {
            clearedAddresses.push({
              ...address,
              private_overrides: {},
              is_deleted: null,
            });
          }
        }
      }
      if (clearedAddresses.length > 0) {
        updateAddresses.mutate({ addresses: clearedAddresses });
      }
    };

    confirmDialog({
      message:
        "This will clear all manual edits on every address and un-delete any addresses that you have deleted. This cannot be undone.",
      header: "Clear All Address Edits",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Clear All",
      rejectLabel: "Cancel",
      accept,
    });
  }, [sovs, updateAddresses]);

  const onCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<RawAddressWithIndices>) => {
      if (!event.data || event.oldValue === event.newValue) return;

      const rowData = event.data;
      const oldAddress = rowData.__extras.fullAddress;

      // Build a RawAddress from the current grid row data (which now has the edited value)
      const { __extras, ...editedData } = rowData;
      void __extras;

      const { newAddress, fieldNameEdits } = applyDeltasAsOverride(
        oldAddress,
        editedData
      );
      logUIEvent("asgard_click_save_sov_edits", {
        field_name_edits: fieldNameEdits,
      });
      if (!isEqual(newAddress, oldAddress)) {
        updateAddresses.mutateAsync({ addresses: [newAddress] });
      }
    },
    [logUIEvent, updateAddresses]
  );

  const onDeleteAddress = useCallback(
    (address: RawAddressWithIndices) => {
      const accept = () => {
        const fullAddress = address.__extras.fullAddress;
        fullAddress.is_deleted = true;
        updateAddresses.mutate({ addresses: [fullAddress] });
      };

      confirmDialog({
        message: "Are you sure you want to delete this address?",
        header: "Delete Address",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        accept,
      });
    },
    // react-query rebuilds the mutation object every render; depend on the
    // stable mutate fn so columnDefs need not rebuild each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
    [updateAddresses.mutate]
  );

  const onCustomAction = useCallback(
    (action: CustomAction, data: RawAddressWithIndices) => () => {
      switch (action) {
        case "open_street_view_modal":
          if (data.lat && data.long) {
            setStreetViewAddress(data.__extras.fullAddress);
            setStreetViewModalVisible({
              lat: Number(data.lat),
              long: Number(data.long),
              street_address: data.street_address,
              location_description: data.location_description,
            });
          }
          break;
      }
    },
    []
  );

  const locationCount = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? Object.keys(groupBy(fullAddresses, "premises_number")).length
    : fullAddresses.length;

  // getAddressColumns (via perClientWithLob) builds fresh arrays on every
  // call. Memoize so columnDefs need not rebuild on every render.
  const addressColumns = useMemo(
    () => getAddressColumns(specialtyPropertyInfo.lob),
    [specialtyPropertyInfo.lob]
  );
  const isRerunningRemotely = useThisReportIsUpdating(safeResponse.report_id);
  const rerunningMessage = useRerunningMessage({
    update: isRerunningRemotely?.updating,
  });

  // ITV square footage helper
  const itvSquareFootage = useCallback((data: RawAddressWithIndices) => {
    const buildingValue = Number(data.building_value);
    if (Number.isNaN(buildingValue)) {
      return "";
    }
    const squareFootage = Number(data.square_footage);
    if (Number.isNaN(squareFootage) || squareFootage === 0) {
      return "";
    }
    return formatExactMoneyDollars(buildingValue / squareFootage);
  }, []);

  const isUpdatingAddress = useCallback(
    (data: RawAddressWithIndices) => {
      return (
        updateAddresses.isPending &&
        updateAddresses.variables.addresses.some(
          (a) => a.address_id === data.__extras.fullAddress.address_id
        )
      );
    },
    [updateAddresses.isPending, updateAddresses.variables]
  );

  const [quickFilterText, setQuickFilterText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Sourced from the report-scoped provider so they survive leaving and
  // returning to the SOV tab. See SovGridStateContext.
  const {
    gridStateRef,
    expandedGroups,
    setExpandedGroups,
    freezeColumns,
    setFreezeColumns,
    viewId,
    setViewId,
  } = useSovGridState();

  // The view lays out the client's catalog: which columns, in what order,
  // under which spanning headers. See sovViews.ts.
  const viewColumns = useMemo(
    () => sovViewById(viewId).layout(addressColumns[clientId] ?? []),
    [viewId, addressColumns, clientId]
  );

  // Build AG Grid column definitions from the existing SovColumnProps.
  const columnDefs: (
    | ColDef<RawAddressWithIndices>
    | ColGroupDef<RawAddressWithIndices>
  )[] = useMemo(() => {
    const cols: (
      | ColDef<RawAddressWithIndices>
      | ColGroupDef<RawAddressWithIndices>
    )[] = [];

    // Consecutive columns sharing a headerGroup nest under one spanning
    // header that stays together when columns are dragged.
    const pushColDef = (
      colDef: ColDef<RawAddressWithIndices>,
      headerGroup: string | undefined
    ) => {
      if (!headerGroup) {
        cols.push(colDef);
        return;
      }
      const last = cols[cols.length - 1];
      if (last && "children" in last && last.headerName === headerGroup) {
        last.children.push(colDef);
        return;
      }
      cols.push({
        groupId: `__group_${cols.length}`,
        headerName: headerGroup,
        marryChildren: true,
        children: [colDef],
      });
    };

    // Delete action column (trash icon) + search trigger in header
    cols.push({
      colId: "actions-delete",
      headerName: "",
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      pinned: freezeColumns ? "left" : null,
      lockPinned: true,
      sortable: false,
      editable: false,
      headerComponent: () => (
        <div
          className={styles.searchIconHeader}
          onClick={() => {
            setSearchOpen((prev) => {
              if (prev) {
                setQuickFilterText("");
              }
              return !prev;
            });
          }}
        >
          <i
            className={searchOpen ? "pi pi-times" : "pi pi-search"}
            style={{ fontSize: "14px", cursor: "pointer" }}
          />
        </div>
      ),
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      cellRenderer: (params: { data: RawAddressWithIndices | undefined }) => {
        if (!params.data) return null;
        return (
          <Button
            onClick={() => onDeleteAddress(params.data!)}
            className={styles.buttonSmall}
            icon="pi pi-trash"
            severity="danger"
            text
            disabled={!!isRerunningRemotely}
            size="small"
            style={{
              position: "static",
            }}
          />
        );
      },
    });

    // Data columns from addressColumns config
    const buildColDef = (
      columnInfo: SovColumnProps,
      groupColor?: string
    ): ColDef<RawAddressWithIndices> => {
      const field = columnInfo.field;
      const isSortable = !columnInfo.isIconValue;
      const parsedWidth = columnInfo.width + AG_GRID_COL_PADDING;
      const needsStringConversion =
        columnInfo.agEditor === "agNumberCellEditor" &&
        field &&
        field !== "tiv";
      const shouldPin = freezeColumns && FROZEN_COLUMN_IDS.has(columnInfo.id);

      const accentStyle = groupColor
        ? { borderTop: `2px solid ${groupColor}` }
        : undefined;

      return {
        colId: columnInfo.id,
        field: field,
        headerName: columnInfo.headerLabel ?? columnInfo.columnName,
        // initialWidth, not width: AG Grid's _updateColumnState re-applies a
        // colDef.width on every columnDefs update, clobbering a manual resize.
        // initialWidth seeds the width once and is preserved across updates.
        initialWidth: parsedWidth,
        minWidth: parsedWidth,
        pinned: shouldPin ? "left" : null,
        lockPinned: true,
        sortable: isSortable,
        comparator: naturalSortComparator,
        ...(columnInfo.filterable ? { filter: SetFilter } : {}),
        ...(accentStyle ? { headerStyle: accentStyle } : {}),
        editable:
          !!columnInfo.agEditor &&
          !!field &&
          !isRerunningRemotely &&
          !updateAddresses.isPending,
        cellEditor:
          columnInfo.agEditor === "agFilterableSelectCellEditor"
            ? FilterableSelectCellEditor
            : columnInfo.agEditor,
        cellEditorParams: columnInfo.agEditorParams,
        ...(columnInfo.isIconValue
          ? {
              cellStyle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }
          : {}),
        valueGetter: (params) => {
          if (!params.data) return undefined;
          if (columnInfo.columnName === "ITV") {
            return itvSquareFootage(params.data);
          }
          return columnInfo.getValue(
            params.data,
            params.context as SovGridContext
          );
        },
        ...(needsStringConversion ||
        columnInfo.agEditor === "agFilterableSelectCellEditor"
          ? {
              valueSetter: (params) => {
                if (!params.data || !field) return false;
                const newVal = params.newValue;
                const row = params.data as Record<string, unknown>;
                const oldRawVal = row[field];
                const normalised =
                  newVal == null || newVal === "" ? null : String(newVal);
                if (normalised === (oldRawVal ?? null)) return false;
                row[field] = normalised;
                if (field === "construction_code" && newVal) {
                  const { codeType, description } = resolveConstructionCodeEdit(
                    String(newVal)
                  );
                  row.construction_code_type = codeType;
                  row.construction_description = description;
                }
                return true;
              },
            }
          : {}),
        cellRenderer: (params: {
          data: RawAddressWithIndices | undefined;
          value: unknown;
          context: SovGridContext;
        }) => {
          if (!params.data) return null;
          const data = params.data;

          const effectiveColumnInfo =
            columnInfo.columnName === "ITV"
              ? { ...columnInfo, getValue: () => itvSquareFootage(data) }
              : columnInfo;

          return (
            <CellBody
              columnInfo={effectiveColumnInfo}
              rowData={data}
              context={params.context}
              isLightMode={isLightMode}
              isUpdatingAddress={isUpdatingAddress(data)}
              onClick={
                columnInfo.customAction
                  ? onCustomAction(columnInfo.customAction, data)
                  : undefined
              }
              isDisabled={false}
            />
          );
        },
        headerComponent: SovHeader,
        headerComponentParams: {
          columnInfo,
          isSortable,
          onBulkEdit: setEditedColumnInfo,
        },
      };
    };

    // Collect columns, handling collapsible groups
    const seenGroups = new Set<string>();

    for (const columnInfo of viewColumns) {
      const group = columnInfo.collapsibleGroup;

      if (!group) {
        pushColDef(buildColDef(columnInfo), columnInfo.headerGroup);
        continue;
      }

      if (!seenGroups.has(group)) {
        seenGroups.add(group);
        const isExpanded = expandedGroups.has(group);
        const groupColor = COLLAPSIBLE_GROUP_COLORS[group];
        const shortLabel = COLLAPSIBLE_GROUP_SHORT_LABELS[group];
        cols.push({
          colId: `__toggle_${group}`,
          headerName: "",
          width: 64,
          minWidth: 64,
          maxWidth: 64,
          sortable: false,
          suppressMovable: true,
          headerComponent: () => (
            <div
              className={styles.columnGroupToggle}
              onClick={() =>
                setExpandedGroups((prev) => {
                  const next = new Set(prev);
                  if (isExpanded) {
                    next.delete(group);
                  } else {
                    next.add(group);
                  }
                  return next;
                })
              }
            >
              <TooltipWrapper
                tooltipText={`${isExpanded ? "Collapse" : "Expand"} ${group} columns`}
              >
                <div className={styles.columnGroupToggleInner}>
                  <i
                    className={`pi pi-angle-${isExpanded ? "left" : "right"} ${styles.columnGroupToggleIcon}`}
                    style={
                      groupColor ? { backgroundColor: groupColor } : undefined
                    }
                  />
                  {shortLabel && (
                    <span className={styles.columnGroupToggleLabel}>
                      {shortLabel}
                    </span>
                  )}
                </div>
              </TooltipWrapper>
            </div>
          ),
          cellRenderer: () => null,
        });
      }

      if (expandedGroups.has(group)) {
        pushColDef(
          buildColDef(columnInfo, COLLAPSIBLE_GROUP_COLORS[group]),
          columnInfo.headerGroup
        );
      }
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [
    viewColumns,
    expandedGroups,
    isLightMode,
    isRerunningRemotely,
    rerunningMessage,
    onCustomAction,
    onDeleteAddress,
    itvSquareFootage,
    isUpdatingAddress,
    updateAddresses.isPending,
    searchOpen,
    freezeColumns,
  ]);

  const getRowId = useCallback(
    (params: { data: RawAddressWithIndices }) =>
      params.data.__extras.fullAddress.address_id,
    []
  );

  const gridRef = useRef<AgGridReact<RawAddressWithIndices>>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const focusedRowIdRef = useRef<string | null>(null);

  // Refresh only the cells whose value depends on each streaming map.
  const streamingColIds = useMemo(() => {
    const byMap: Record<StreamingSource, string[]> = {
      integration: [],
      deductible: [],
      limit: [],
    };
    for (const c of viewColumns) {
      if (c.streamingSource) {
        byMap[c.streamingSource].push(c.id);
      }
    }
    return byMap;
  }, [viewColumns]);

  // Per the *Result.data APIs, we depend on the outer wrapper and read the
  // inner (mutable) map. Header components are re-rendered explicitly via
  // refreshHeader since their content (Loading [N/M] / Error / total) tracks
  // streaming status.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: streamingColIds.integration,
      force: true,
    });
    gridRef.current?.api?.refreshHeader();
  }, [integrationResult.data, integrationStatus, streamingColIds.integration]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: streamingColIds.deductible,
      force: true,
    });
    gridRef.current?.api?.refreshHeader();
  }, [deductibleResult.data, deductibleStatus, streamingColIds.deductible]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: streamingColIds.limit,
      force: true,
    });
    gridRef.current?.api?.refreshHeader();
  }, [limitResult.data, limitStatus, streamingColIds.limit]);

  const applyFocusClass = useCallback((rowId: string | null) => {
    const el = gridContainerRef.current;
    if (!el) return;
    el.querySelectorAll<HTMLElement>(`.${styles.focusedRow}`).forEach((r) =>
      r.classList.remove(styles.focusedRow)
    );
    if (rowId) {
      el.querySelectorAll<HTMLElement>(
        `.ag-row[row-id="${CSS.escape(rowId)}"]`
      ).forEach((r) => r.classList.add(styles.focusedRow));
    }
  }, []);

  const onCellFocused = useCallback(
    (event: CellFocusedEvent) => {
      const newId =
        event.rowIndex != null
          ? (gridRef.current?.api.getDisplayedRowAtIndex(event.rowIndex)?.id ??
            null)
          : null;
      focusedRowIdRef.current = newId;
      applyFocusClass(newId);
    },
    [applyFocusClass]
  );

  const rowClassRules = useMemo(
    () => ({
      [styles.focusedRow]: (params: RowClassParams<RawAddressWithIndices>) =>
        params.data?.__extras.fullAddress.address_id ===
        focusedRowIdRef.current,
    }),
    []
  );

  // Snapshot the grid's column/sort/filter state on every change so it can be
  // restored via initialState when the grid remounts (tab switch). focusedCell
  // is dropped so returning to the tab doesn't re-focus a stale cell.
  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent<RawAddressWithIndices>) => {
      const { focusedCell, ...rest } = event.state;
      void focusedCell;
      gridStateRef.current = rest;
    },
    [gridStateRef]
  );

  return (
    <Card
      title="Statement of Values"
      subTitle={
        <div className={styles.subtitleContainer}>
          <div className={styles.subtitleContentsContainerVertical}>
            <span>
              {"Total Insured Value: "}
              <b>{formatExactMoneyDollars(unfilteredTotalTIV)}</b>
            </span>
            <span>
              {"Location Count: "}
              <b>{locationCount}</b>
            </span>
          </div>
          <div className={styles.subtitleContentsContainer}>
            <EditSheets
              reportId={specialtyPropertyInfo.report_id}
              sovs={sovs}
            />
            <SovViewSwitcher viewId={viewId} onChange={setViewId} />
            <Button
              text
              raised
              severity="secondary"
              icon="pi pi-chart-bar"
              onClick={() => setPivotVisible(true)}
              tooltip="Pivot tables"
              tooltipOptions={{ showDelay: 500 }}
            />
            <Button
              text
              raised
              severity={freezeColumns ? "info" : "secondary"}
              icon={freezeColumns ? "pi pi-lock" : "pi pi-lock-open"}
              onClick={() => setFreezeColumns((prev) => !prev)}
              tooltip={
                freezeColumns
                  ? "Unfreeze address columns"
                  : "Freeze address columns"
              }
              tooltipOptions={{ showDelay: 500 }}
            />
            {setMapVisible ? (
              <Button
                text
                raised
                severity="secondary"
                icon="pi pi-map"
                label="Show map"
                onClick={() => setMapVisible(true)}
              />
            ) : null}
            <IfFeatureEnabled feature="reprocess-button" user={currentUser}>
              <Button
                text
                raised
                label="Re-process"
                severity="danger"
                icon={
                  isRerunningRemotely ? "pi pi-spin pi-spinner" : "pi pi-replay"
                }
                onClick={() => setReprocessDialogVisible(true)}
                disabled={isRerunningRemotely !== undefined}
              />
            </IfFeatureEnabled>
            <Menu
              model={(
                [
                  {
                    label: "Add Location",
                    icon: "pi pi-plus",
                    command: () => setAddAddressModalVisible(true),
                  },
                  {
                    label: "Clear Address Edits",
                    icon: "pi pi-eraser",
                    disabled: !hasAnyAddressOverrides,
                    command: clearAllAddressEdits,
                  },
                  {
                    label: "Download SOV",
                    icon: "pi pi-download",
                    command: async () => {
                      const blob = await downloadReportHistory(
                        [reportId],
                        "complex_location"
                      );
                      const dateString = new Date()
                        .toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                        .replace(/\//g, "-");
                      downloadBlob(`SOV ${dateString}.xlsx`, blob);
                    },
                  },
                  featureEnabled(clientId, "tiv-reconciliation-functions", {
                    user: currentUser,
                  }) && sovs.some((sov) => sov.reconciliation_function)
                    ? {
                        label: "TIV Recon",
                        icon: "pi pi-code",
                        command: () => setReconciliationModalVisible(true),
                      }
                    : undefined,
                ] as (MenuItem | undefined)[]
              ).filter((item): item is MenuItem => item !== undefined)}
              popup
              ref={overflowMenuRef}
            />
            <Button
              icon="pi pi-ellipsis-v"
              onClick={(e) => overflowMenuRef.current?.toggle(e)}
              text
              raised
              severity="secondary"
              tooltip="More actions"
              tooltipOptions={{ showDelay: 500 }}
            />
          </div>
        </div>
      }
    >
      <Dialog
        visible={pivotVisible}
        onHide={() => setPivotVisible(false)}
        header="Pivot Tables"
        maximizable
        className={styles.pivotDialog}
      >
        <PivotPanel
          key={reportId}
          addresses={fullAddresses}
          context={sovGridContext}
          reportId={reportId}
        />
      </Dialog>
      <div
        ref={gridContainerRef}
        style={{ height: "70vh", width: "100%" }}
        className={styles.gridContainer}
      >
        {searchOpen && (
          <div className={styles.searchOverlay}>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                ref={searchInputRef}
                value={quickFilterText}
                onChange={(e) => setQuickFilterText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuickFilterText("");
                    setSearchOpen(false);
                  }
                }}
                placeholder="Search all columns..."
                style={{ width: "250px", paddingLeft: "2rem" }}
                size="small"
                autoFocus
              />
            </span>
          </div>
        )}
        <AgGridReact<RawAddressWithIndices>
          ref={gridRef}
          theme={isLightMode ? agThemeLight : agThemeDark}
          modules={[AllCommunityModule]}
          rowData={fullAddresses}
          columnDefs={columnDefs}
          context={sovGridContext}
          getRowId={getRowId}
          initialState={gridStateRef.current}
          onStateUpdated={onStateUpdated}
          onCellEditingStopped={onCellEditingStopped}
          onCellFocused={onCellFocused}
          rowClassRules={rowClassRules}
          headerHeight={60}
          groupHeaderHeight={28}
          rowHeight={36}
          alwaysShowHorizontalScroll={true}
          suppressHeaderFocus={true}
          quickFilterText={quickFilterText}
        />
      </div>
      <ColumnEditModal
        columnInfo={editedColumnInfo}
        visible={editedColumnInfo !== null}
        setVisible={() => setEditedColumnInfo(null)}
        fullAddresses={fullAddresses}
        context={sovGridContext}
        onHide={() => setEditedColumnInfo(null)}
        updateAddresses={updateAddresses}
        onSaved={() => setEditedColumnInfo(null)}
      />
      <ReprocessDialog
        reportId={specialtyPropertyInfo.report_id}
        visible={reprocessDialogVisible}
        onHide={() => setReprocessDialogVisible(false)}
        isPending={updateReport.isPending}
        onConfirm={(selection) => {
          updateReport.mutate({
            rerunReport: true,
            rerunComponents: selection?.components,
            rerunAttachments: selection?.attachments,
          });
          setReprocessDialogVisible(false);
        }}
      />
      <BulkEditModal
        state={bulkEditModalField}
        onHide={() => setBulkEditModalField(undefined)}
        report={specialtyPropertyInfo.report_json}
        isLightMode={isLightMode}
      />
      {streetViewModalVisible ? (
        /* This component does not respond to "state" prop changes cleanly, so remount it every time to avoid stale state. */
        <StreetViewModal
          initialState={streetViewModalVisible}
          onHide={() => {
            setStreetViewModalVisible(undefined);
            setStreetViewAddress(null);
          }}
          reportId={reportId}
          onRecenter={async (lat, long) => {
            const addr = streetViewAddress;
            if (!addr) return;
            addr.private_overrides["lat"] = lat.toString();
            addr.private_overrides["long"] = long.toString();
            await updateAddresses.mutateAsync({ addresses: [addr] });
          }}
        />
      ) : null}
      <AddAddressModal
        visible={addAddressModalVisible}
        setVisible={setAddAddressModalVisible}
        additionalStyles={{}}
        lob={specialtyPropertyInfo.lob}
        report={safeResponse}
      />
      <ReconciliationFunctionModal
        visible={reconciliationModalVisible}
        setVisible={setReconciliationModalVisible}
        sovs={sovs}
      />
    </Card>
  );
};
